import { EmployeeNotificationType } from 'src/generated/mloop-graphql';
import type { EmployeeNotificationsQuery_employeeNotifications_EmployeeNotificationsResult_items_EmployeeNotification as EmployeeNotification } from 'src/generated/mloop-graphql';

import type { InboxNotificationView } from './types';

const NOTIFICATION_URL_BASE = 'https://app.modernloop.io';

const TASK_NOTIFICATION_TYPES = [
  EmployeeNotificationType.TaskAssigned,
  EmployeeNotificationType.TaskMention,
  EmployeeNotificationType.TaskNoteAdded,
  EmployeeNotificationType.TaskChangeRequested,
  EmployeeNotificationType.TaskInterviewerDeclined,
  EmployeeNotificationType.TaskCandidateDeclined,
  EmployeeNotificationType.TaskAvailabilityRequested,
  EmployeeNotificationType.TaskAvailabilitySubmittedByEmployee,
  EmployeeNotificationType.TaskAvailabilitySubmittedByCandidate,
  EmployeeNotificationType.TaskSelfScheduleRequested,
  EmployeeNotificationType.TaskSelfScheduleBooked,
  EmployeeNotificationType.TaskSelfScheduleChanged,
  EmployeeNotificationType.TaskSelfScheduleCanceled,
  EmployeeNotificationType.TaskMarkedUrgent,
  EmployeeNotificationType.TaskRcAdjustInterviewerRsvp,
  EmployeeNotificationType.TaskReadyForCandidateComm,
  EmployeeNotificationType.TaskQueueEmployeeMembershipChanged,
  EmployeeNotificationType.TaskQueueTaskUnassigned,
  EmployeeNotificationType.TaskAutomateCreateFailure,
  EmployeeNotificationType.TaskAgentQuestion,
  EmployeeNotificationType.TaskCandidateTentative,
  EmployeeNotificationType.TaskInterviewerTentative,
  EmployeeNotificationType.TaskInterviewerDeactivated,
  EmployeeNotificationType.TaskDebriefAttendeeTentative,
  EmployeeNotificationType.TaskDebriefAttendeeDeactivated,
  EmployeeNotificationType.TaskDebriefAttendeeDeclined,
] as const;
const INTERVIEW_NOTIFICATION_TYPES = [
  EmployeeNotificationType.InterviewTrainingUpdate,
  EmployeeNotificationType.InterviewerRsvpReminder,
  EmployeeNotificationType.InterviewerUpcomingInterview,
] as const;
const INTERVIEW_MODULE_NOTIFICATION_TYPES = [
  EmployeeNotificationType.InterviewModuleShadowAssignedToInterview,
  EmployeeNotificationType.InterviewModuleReverseShadowAssignedToInterview,
  EmployeeNotificationType.InterviewModuleTrainedAssignedToInterview,
  EmployeeNotificationType.InterviewModuleShadowToReverseShadowApprovalRequired,
  EmployeeNotificationType.InterviewModuleShadowToTrainedApprovalRequired,
  EmployeeNotificationType.InterviewModuleReverseShadowToTrainedApprovalRequired,
  EmployeeNotificationType.InterviewModuleShadowGraduatedToReverseShadow,
  EmployeeNotificationType.InterviewModuleShadowGraduatedToTrained,
  EmployeeNotificationType.InterviewModuleShadowGraduationRejected,
  EmployeeNotificationType.InterviewModuleReverseShadowGraduatedToTrained,
  EmployeeNotificationType.InterviewModuleReverseShadowGraduationRejected,
] as const;
const HIRING_PROCESS_NOTIFICATION_TYPES = [
  EmployeeNotificationType.HiringProcessWarning,
  EmployeeNotificationType.HiringProcessMismatch,
] as const;
const CANDIDATE_NOTIFICATION_TYPES = [
  EmployeeNotificationType.CandidateAnonymizationCompleted,
  EmployeeNotificationType.CandidateDataExportCompleted,
] as const;

export const NOTIFICATION_TYPE_LABEL_KEYS = Object.fromEntries([
  ...TASK_NOTIFICATION_TYPES.map((type) => [type, 'hiring-dashboard-nav:nav.notificationInbox.type.task']),
  ...INTERVIEW_NOTIFICATION_TYPES.map((type) => [type, 'hiring-dashboard-nav:nav.notificationInbox.type.interview']),
  ...INTERVIEW_MODULE_NOTIFICATION_TYPES.map((type) => [
    type,
    'hiring-dashboard-nav:nav.notificationInbox.type.interviewModule',
  ]),
  ...HIRING_PROCESS_NOTIFICATION_TYPES.map((type) => [
    type,
    'hiring-dashboard-nav:nav.notificationInbox.type.hiringProcess',
  ]),
  ...CANDIDATE_NOTIFICATION_TYPES.map((type) => [type, 'hiring-dashboard-nav:nav.notificationInbox.type.candidate']),
]) as Record<EmployeeNotificationType, string>;

/** Parses the schema's string field into a known notification enum at the GraphQL boundary. */
const parseNotificationType = (value: string): EmployeeNotificationType | null =>
  Object.values(EmployeeNotificationType).find((type) => type === value) ?? null;

/** Converts the backend's full Hiring Dashboard URL into a route that React Router can open. */
export const parseNotificationDestination = (ctaLink: string | null | undefined): string | null => {
  if (!ctaLink || !URL.canParse(ctaLink, NOTIFICATION_URL_BASE)) {
    return null;
  }

  const url = new URL(ctaLink, NOTIFICATION_URL_BASE);
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return null;
  }

  return `${url.pathname}${url.search}${url.hash}`;
};

/** Returns the localized category label for a notification type. */
export const getNotificationTypeLabel = (
  t: (key: string) => string,
  notificationType: EmployeeNotificationType | null
) =>
  t(
    notificationType
      ? NOTIFICATION_TYPE_LABEL_KEYS[notificationType]
      : 'hiring-dashboard-nav:nav.notificationInbox.type.notification'
  );

/** Returns all concrete notification types that share a category label key. */
export const getNotificationTypesForLabelKey = (labelKey: string): EmployeeNotificationType[] =>
  Object.entries(NOTIFICATION_TYPE_LABEL_KEYS)
    .filter(([, key]) => key === labelKey)
    .map(([type]) => type as EmployeeNotificationType);

/** Maps the GraphQL notification shape once into the display-ready inbox model. */
export const mapEmployeeNotificationToView = (
  t: (key: string) => string,
  notification: EmployeeNotification
): InboxNotificationView => {
  const notificationType = parseNotificationType(notification.notificationType);
  return {
    id: notification.id,
    plainText: notification.plainText,
    actorName: notification.actorEmployee?.fullName ?? null,
    actorImageUrl:
      notification.actorEmployee?.internalProfilePictureUrl ?? notification.actorEmployee?.photoUrl ?? null,
    typeLabel: getNotificationTypeLabel(t, notificationType),
    notificationType,
    createdAt: notification.createdAt,
    isRead: notification.isRead,
    ctaLink: parseNotificationDestination(notification.ctaLink),
  };
};
